import { useMemo, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import DetailedMetricRow from "../../../core/blocks/DetailedMetricRow";
import AdvertisingIcon from "../../../../assets/images/icons/placement.svg";
import ThemeLine from "../ThemeLine";
import { useAppSelector } from "../../../../redux/hooks";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { useGetV4CategoryReportByDate } from "../../../../modules/perf_reports/api/category/v4CategoryReportByDate";
import { useGetV4CategoryReportTotals } from "../../../../modules/perf_reports/api/category/v4CategoryReportTotals";

interface PlacementCardProps {
  category?: string | null;
}

const PlacementCard = ({ category = null }: PlacementCardProps): ReactElement => {
  const { t } = useTranslation(["merchandising"]);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  // Fetch category data
  const categoryByDateOptionsGetter = useGetV4CategoryReportByDate();
  const categoryByDateReportQuery = useQuery({
    ...categoryByDateOptionsGetter({
      filters: category ? [{ id: "category_id", value: { op: "=", value: category } }] : [],
    }),
  });

  const categoryTotalOptionsGetter = useGetV4CategoryReportTotals();
  const categoryTotalsReportQuery = useQuery({
    ...categoryTotalOptionsGetter({
      filters: category ? [{ id: "category_id", value: { op: "=", value: category } }] : [],
    }),
  });

  const data = useMemo(() => {
    if (categoryTotalsReportQuery.data?.data && categoryByDateReportQuery.data?.data) {
      return {
        total: categoryTotalsReportQuery.data.data,
        date: categoryByDateReportQuery.data.data,
      };
    }
    return null;
  }, [categoryTotalsReportQuery.data?.data, categoryByDateReportQuery.data?.data]);

  const metrics = useMemo(() => {
    if (channel === "shopify") {
      return [];
    }

    return [getMetricSpec("ad_sales"), getMetricSpec("ad_spend"), getMetricSpec("roas"), getMetricSpec("clicks")];
  }, [channel]);

  return (
    <DetailedMetricRow
      title={t(($) => $.merchandising["Advertising"])}
      to={category ? `/user/advertising/dashboard/category/${category}` : "/user/advertising/dashboard"}
      byline={
        <>
          <ThemeLine>{t(($) => $.merchandising["Placement"])}</ThemeLine>
          {t(($) => $.merchandising["Create and manage media plans"])}
        </>
      }
      totals={{ data: data?.total, loading: categoryTotalsReportQuery.isLoading }}
      timeline={{ data: data?.date, loading: categoryByDateReportQuery.isLoading }}
      metrics={metrics}
      icon={AdvertisingIcon}
    />
  );
};

export default PlacementCard;
